import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import {
  Sidebar as KitSidebar,
  SidebarBrand,
  SidebarNav,
  SidebarTrigger,
  useSidebar,
  type AccentTone,
  type SidebarLinkProps,
  type SidebarNavEntry,
} from '@shining-technologies/ui';
import { HivezTile } from '@/components/icons/HivezMark';
import { NAV, reachableNavItems, type NavItem } from '@/app/nav';
import { usePermission } from '@/hooks/usePermission';
import type { Action } from '@/lib/permissions';
import { cn } from '@/lib/utils';

type BadgeVariant = NonNullable<NavItem['badge']>['variant'];

/**
 * Legacy badge variants (`app/nav.ts`) → the kit's sidebar badge tone, plus the
 * AA-safe `*-text` ink for tones whose fill colour fails as text (the kit
 * colours a toned sidebar badge's text with the raw tone — 2.1:1 for warning).
 */
const BADGE_STYLE: Record<NonNullable<BadgeVariant>, { tone: AccentTone; ink?: string }> = {
  default: { tone: 'primary' },
  secondary: { tone: 'neutral' },
  outline: { tone: 'neutral' },
  success: { tone: 'success', ink: 'text-success-text' },
  warning: { tone: 'warning', ink: 'text-warning-text' },
  destructive: { tone: 'destructive', ink: 'text-destructive-text' },
};

/**
 * The permission-filtered primary navigation as the kit's `SidebarNav` tree:
 * one section per `NAV` group, empty groups dropped.
 */
function buildNav(can: (a: Action) => boolean): SidebarNavEntry[] {
  return NAV.flatMap((group): SidebarNavEntry[] => {
    const items = reachableNavItems(group.items, can);
    if (items.length === 0) return [];
    return [
      {
        type: 'section',
        id: `group:${group.label}`,
        label: group.label,
        items: items.map((item) => {
          const Icon = item.icon;
          const badge = item.badge ? BADGE_STYLE[item.badge.variant ?? 'secondary'] : undefined;
          return {
            id: item.path,
            label: item.label,
            href: item.path,
            icon: <Icon aria-hidden />,
            // FIX(admin-nav-not-built-badge): Billing/Integrations are
            // mock-backed surfaces with no visual signal in the nav. Before:
            // none. After: a "Not built" warning-tone badge next to the label —
            // shown in the desktop sidebar and the mobile drawer (same tree);
            // in the collapsed rail the kit shrinks it to a dot (the tooltip
            // still shows just the label).
            badge: item.badge ? <span className={badge?.ink}>{item.badge.label}</span> : undefined,
            badgeTone: badge?.tone,
          };
        }),
      },
    ];
  });
}

/** Every sidebar link goes through react-router (the kit passes `href`). */
function renderRouterLink({ href, ...props }: SidebarLinkProps) {
  return <Link to={href} {...props} />;
}

/**
 * The sidebar footer control: collapse/expand the desktop rail, or close the
 * mobile drawer (the header trigger is inert while the drawer is open, so the
 * drawer needs its own visible way out). The kit trigger names itself
 * "Collapse sidebar" / "Expand sidebar" / "Close navigation"; the visible text
 * is a prefix of that name.
 */
function SidebarFooterControl() {
  const { rail, isMobile } = useSidebar();
  return (
    <SidebarTrigger
      size="sm"
      className={cn('w-full', rail ? 'justify-center px-0' : 'justify-start')}
      // In the drawer this button is only reachable while the drawer is open,
      // so it is always the close control (the kit would call it "Open
      // navigation" while the hidden drawer is closed). Spread, not
      // `aria-label={undefined}`: an explicit undefined would replace the
      // kit's computed "Collapse sidebar" / "Expand sidebar".
      {...(isMobile ? { 'aria-label': 'Close navigation' } : {})}
    >
      {isMobile ? (
        <>
          <X aria-hidden />
          <span>Close</span>
        </>
      ) : rail ? (
        <PanelLeftOpen aria-hidden />
      ) : (
        <>
          <PanelLeftClose aria-hidden />
          <span>Collapse</span>
        </>
      )}
    </SidebarTrigger>
  );
}

/**
 * The app sidebar: the kit `Sidebar` (a direct child of the kit `AppShell`),
 * collapsing to an icon rail on desktop and a modal drawer below 48rem (the
 * `md` breakpoint). The rail state is the persisted `useUiStore`
 * `sidebarCollapsed`, wired through the `SidebarProvider` in `AppShell`.
 *
 * FIX(admin-mobile-shell): Before: always rendered at 240px (64px collapsed),
 * which at 375px left ~90px for the page. After: below `md` the sidebar leaves
 * the layout and becomes a drawer, opened from the header's "Open navigation"
 * trigger (`MobileNavTrigger`). Desktop unchanged.
 */
export function Sidebar() {
  const { can } = usePermission();
  const { pathname } = useLocation();
  const items = useMemo(() => buildNav((a) => can(a)), [can]);

  return (
    <KitSidebar
      aria-label="Navigation"
      width="15rem"
      railWidth="4rem"
      appearance="primary"
      // Sits below the impersonation banner when one is shown — see
      // FIX(admin-shell-banner-offset) in ImpersonationBanner.
      className="top-(--admin-banner-h,0px) h-[calc(100dvh-var(--admin-banner-h,0px))]"
      renderLink={renderRouterLink}
      header={<SidebarBrand name="The Hivez" logo={<HivezTile className="size-9" />} />}
      footer={<SidebarFooterControl />}
    >
      <SidebarNav aria-label="Primary" items={items} currentPath={pathname} />
    </KitSidebar>
  );
}

/**
 * Mobile navigation trigger (below `md`): opens the sidebar drawer. Rendered
 * only while the kit reports the drawer layout, so it matches the sidebar's
 * own breakpoint exactly.
 */
export function MobileNavTrigger() {
  const { isMobile } = useSidebar();
  if (!isMobile) return null;
  return (
    <SidebarTrigger>
      <Menu aria-hidden />
    </SidebarTrigger>
  );
}
